import { useMutation, UseMutationResult, useQuery, UseQueryResult } from '@tanstack/react-query'
import { getReturnReceiptItems } from '@/services/return/returnReceipt/apis'
import { ReturnReceiptFilter } from '@/services/return/returnReceipt/types'
import { receiveReturnReceipt } from './apis'
import { ReceiveScanReq, ReceiveScanRes, ScanResultRow, toScanResultRowFromItem } from './types'

const PATH = ['return', 'returnReceiptReceive'] as const

export const returnReceiptReceiveQueryKeys = {
  all: PATH,
  receive: () => [...PATH, 'RECEIVE'] as const,
  history: (filter: ReceiptHistoryFilter | null) => [...PATH, 'HISTORY', filter] as const,
}

/** 반송장 스캔 1회 — 화면이 스캔마다 호출한다. */
export const useReceiveReturnReceipt = (): UseMutationResult<ReceiveScanRes, Error, ReceiveScanReq> =>
  useMutation({
    mutationKey: returnReceiptReceiveQueryKeys.receive(),
    mutationFn: receiveReturnReceipt,
  })

/**
 * RGR14 이력 조회 조건 — 화면 필터 두 칸을 그대로 옮긴 모양이다.
 * 날짜는 압축형(`yyyyMMdd`) 이다. BE 가 `UI_RETURN_INVOICE.CLS_DATE` 문자열과 그대로 비교한다
 * (moms-be `ReturnReceiptJpaRepository.commonPredicate`).
 */
export interface ReceiptHistoryFilter {
  centerNm: string
  clsDateFrom: string
  clsDateTo: string
}

type HistoryResult = Omit<UseQueryResult<ScanResultRow[], Error>, 'data'> & { rows: ScanResultRow[] }

/**
 * 스캔시각 내림차순 — 최신 스캔이 맨 위다.
 *
 * 서버는 순서를 정해주지 않는다(moms-be `ReturnReceiptJpaRepository.findItems` 에 ORDER BY 없음.
 * 같은 파일 `findSummaries` 는 정렬한다). 표에도 sorter 나 스캔시각 컬럼이 없어 사용자가 다시
 * 세울 방법이 없으므로 여기서 세운다 — 새로고침 전까지 이 화면은 항상 최신 스캔이 맨 위였다
 * (`useScanWorkflow` 가 새 행을 앞에 붙인다).
 *
 * `SCAN_DATE` 는 `yyyyMMddHHmmss` 고정폭이라 문자열 비교가 곧 시간순이다. 빈 값(dev 실측 668건 중
 * 117건, 전부 2023년 레거시)은 어떤 숫자 문자열보다 작아 자연히 맨 뒤로 간다 — 시각을 모르는 행을
 * 최신 자리에 올리지 않는다는 뜻이라 그대로 둔다. 같은 값끼리는 Array#sort 가 안정 정렬이라
 * 서버가 준 순서를 유지한다.
 */
const byScanDateDesc = (a: ScanResultRow, b: ScanResultRow): number => {
  if (a.SCAN_DATE === b.SCAN_DATE) return 0
  return a.SCAN_DATE < b.SCAN_DATE ? 1 : -1
}

/**
 * RGR14 센터도착 수령작업 이력 조회. (LOGISPRD-12610)
 *
 * 저장은 `POST /api/v1/return/receipts`(이 서비스), 읽기는 같은 경로의 GET(RGR09 서비스)이다 —
 * 두 화면이 `UI_RETURN_INVOICE` 를 함께 쓰므로 조회 함수는 RGR09 것을 그대로 부른다.
 *
 * 서버 페이징이 없어 조건에 걸린 전건이 한 번에 온다. 등록센터·마감일자 범위를 항상 함께 실어
 * 조회 범위를 묶는다 — `enabled` 가 세 값이 다 있을 때만 켜지는 이유다.
 *
 * 이 조회는 감사로그에 RGR09 로 집계된다. moms-be `ReturnReceiptController.buildAuditContext` 가
 * menuCode 를 `MENU_CODE`("RGR09" 상수)로 고정하고, `ReturnReceiptSearchService.searchItems` 가
 * 조회마다 `recordAuditSafely` 를 무조건 부른다. 이를 바꿀 쿼리 파라미터도 헤더도 없다. 그래서 RGR09
 * 를 연 적 없는 스캔 작업자도 `MOMS_USER_ACCESS_LOG` 에 MENU_CD='RGR09' · API_NM='RGR09_ITEM' 로
 * 남는다 — 화면 진입 1건, 이후 필터를 바꿀 때마다 1건씩 쌓인다.
 *
 * 이 레포의 감사로그 컨벤션은 menuCode=화면 · apiName=그리드다(moms-be
 * `NormalInboundSearchController` 등). 화면 축이 어긋나 있어 "누가 어느 화면을 봤나" 를 로그만으로는
 * 되짚을 수 없다. 바로잡는 방향은 둘이다. (가) moms-be 가 menuCode·apiName 을 요청에서 받는다 —
 * 다른 화면이 같은 조회를 재사용할 때도 통하지만 클라이언트 값이 그대로 감사로그에 적히므로 허용값을
 * 서버가 들고 있어야 한다. (나) RGR14 전용 조회 엔드포인트를 연다 — 감사 축이 경로로 갈려 값 검증이
 * 필요 없지만 조회 로직이 갈라지지 않게 내부는 공유해야 한다. 둘 다 BE 변경이라 LOGISPRD-12610 범위
 * 밖이고, 후속 티켓 LOGISPRD-12665 에서 다룬다.
 *
 * 응답 본문에 수취인 개인정보 3종이 실려 온다 — 화면이 그리는 컬럼보다 응답이 넓다. 감사로그 축과는
 * 원인이 다른 별개 사안이라 따로 적는다. GET 이 `ReturnReceiptItemDto` 를 그대로 반환하는데(moms-be
 * `ReturnReceiptController.getReceiptItems`, `@JsonIgnore` 도 마스킹도 없음) 그 DTO 에 수취인 이름
 * (`trsNm`)·주소(`trsAddr`)·전화번호(`trsPhone`)가 있고, DTO 주석이 `CryptoConverter` 로 복호된
 * 평문이라고 직접 밝힌다(oms-core `dto/returns/ReturnReceiptItemDto.kt`, moms-be main d966ee99 기준).
 * FE `ReturnReceiptItemRes` 가 이 셋을 선언하지 않아 화면에는 안 그리지만 HTTP 응답에는 그대로 온다.
 * 변경 전 RGR14 는 조회 API 를 하나도 부르지 않았으므로 창고 현장 스캔 화면으로 이 페이로드가
 * 내려가는 것은 LOGISPRD-12610 이 만든 경로다 — 진입 1회, 이후 조회조건을 바꿀 때마다 해당 센터·마감
 * 일자 전건이다.
 *
 * 같은 컨트롤러의 엑셀 반출(`downloadReceiptSummariesExcel`)은 비밀번호와 사유 코드를 요구하지만 이
 * GET 에는 그런 게이트가 없다. 다만 그 엑셀이 싣는 것은 HEAD 집계(마감일자·입고센터·수량)라
 * (`ReturnReceiptSummaryExcelDto`) 개인정보 3종이 오는 자리는 아니다 — 같은 데이터의 다른 경로가
 * 아니라 이 컨트롤러에 게이트 있는 반출 경로가 따로 있다는 뜻으로만 읽어야 한다.
 *
 * 응답을 좁히려면 BE 를 고쳐야 한다. LOGISPRD-12665 에서 감사 축을 바로잡을 때 (나) RGR14 전용 조회
 * 엔드포인트를 고르면 그 자리에서 응답 필드도 화면이 쓰는 것만 남길 수 있다. RGR14 작업자가 수취인
 * 개인정보를 받아도 되는지는 이 저장소로 판정할 수 없어 확인을 요청해 둔 상태다 — RGR09 와 권한
 * 대상이 같아 새로 열리는 범위가 없는지도 그 확인에 포함된다.
 */
export const useGetReceiptScanHistory = (filter: ReceiptHistoryFilter | null, enabled: boolean): HistoryResult => {
  const isActive = enabled && Boolean(filter?.centerNm && filter.clsDateFrom && filter.clsDateTo)
  const { data, ...rest } = useQuery({
    queryKey: returnReceiptReceiveQueryKeys.history(filter),
    queryFn: async () => {
      // 이론상 미도달 — `enabled` 가 선행 차단한다. 단언 대신 좁혀서 게이트 밖 강제 실행에도 던지지 않게 둔다.
      if (!filter) return []
      const { centerNm, clsDateFrom, clsDateTo } = filter
      const req: ReturnReceiptFilter = { clsDateFrom, clsDateTo, centerNm, rtnInvoiceNos: [] }
      const res = await getReturnReceiptItems(req)
      return (res.data?.items ?? []).map(toScanResultRowFromItem).sort(byScanDateDesc)
    },
    enabled: isActive,
  })

  // 게이트가 닫히면 행도 없다고 답한다. `enabled: false` 는 조회만 멈출 뿐 캐시에 남은 `data` 를
  // 비우지 않아, 플래그를 켠 뒤 이력을 받은 화면에서 플래그를 꺼도 그 이력이 계속 표에 남는다 —
  // 플래그 off 가 롤백 수단인데 이전 동작으로 돌아가지 않는다. 화면 쪽에 병합 게이트를 하나 더
  // 다는 대신 여기서 막는다. 소비처가 늘어도 같은 실수를 되풀이할 자리가 없다.
  return { rows: isActive ? (data ?? []) : [], ...rest }
}
